import React, { useState } from 'react';
import { X, Upload, Check } from 'lucide-react';
import '../../styles/marketplace.css'; // Ensure styles are available

const CreateListingModal = ({ isOpen, onClose, onSubmit, isSubmitting, productToEdit, defaultCurrency = 'SAR' }) => {
    // Reset or populate form when modal opens or productToEdit changes
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: '',
        currency: defaultCurrency,
        category: 'food',
        image: '',
        stock: '10' // Default stock
    });

    React.useEffect(() => {
        if (isOpen) {
            if (productToEdit) {
                setFormData({
                    title: productToEdit.title,
                    description: productToEdit.description,
                    price: productToEdit.price,
                    currency: productToEdit.currency || defaultCurrency,
                    category: productToEdit.category,
                    image: productToEdit.image,
                    stock: productToEdit.stock || 0
                });
            } else {
                setFormData({
                    title: '',
                    description: '',
                    price: '',
                    currency: defaultCurrency,
                    category: 'food',
                    image: '',
                    stock: '10'
                });
            }
        }
    }, [isOpen, productToEdit, defaultCurrency]);

    const categories = [
        { id: 'food', label: 'Food & Treats' },
        { id: 'toys', label: 'Toys' },
        { id: 'accessories', label: 'Accessories' },
        { id: 'bedding', label: 'Bedding' },
        { id: 'habitats', label: 'Habitats & Cages' },
        { id: 'healthcare', label: 'Healthcare' }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!isOpen) return null;

    const isEdit = !!productToEdit;

    return (
        <div className="mp-modal-overlay">
            <div className="mp-modal-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', borderBottom: '1px solid #E5E7EB' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>
                        {isEdit ? 'Edit Product' : 'Add New Product'}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{ padding: '8px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280' }}
                    >
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                    {/* Image Upload Mock */}
                    <div className="mp-form-group">
                        <label className="mp-form-label">Product Image URL</label>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ flexGrow: 1 }}>
                                <input
                                    type="text"
                                    name="image"
                                    value={formData.image}
                                    onChange={handleChange}
                                    placeholder="https://example.com/image.jpg"
                                    className="mp-form-input"
                                    required
                                />
                                <p style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280' }}>For now, paste a direct image link.</p>
                            </div>
                            <div style={{ width: '96px', height: '96px', background: '#F3F4F6', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {formData.image ? (
                                    <img src={formData.image} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <Upload className="text-gray-400" />
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                        <div className="mp-form-group">
                            <label className="mp-form-label">Product Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className="mp-form-input"
                                placeholder="e.g. Premium Dog Food"
                                required
                            />
                        </div>

                        <div className="mp-form-group">
                            <label className="mp-form-label">Category</label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className="mp-form-select"
                            >
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                        <div className="mp-form-group">
                            <label className="mp-form-label">Price</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className="mp-form-input"
                                    placeholder="0.00"
                                    required
                                    style={{ flexGrow: 1 }}
                                />
                                <div style={{
                                    padding: '12px', background: '#F3F4F6', border: '1px solid #E5E7EB',
                                    borderRadius: '12px', color: '#6B7280', fontWeight: '600',
                                    display: 'flex', alignItems: 'center'
                                }}>
                                    {formData.currency}
                                </div>
                            </div>
                        </div>
                        <div className="mp-form-group">
                            <label className="mp-form-label">Stock Quantity</label>
                            <input
                                type="number"
                                name="stock"
                                value={formData.stock}
                                onChange={handleChange}
                                min="0"
                                step="1"
                                className="mp-form-input"
                                placeholder="e.g. 10"
                                required
                            />
                        </div>
                    </div>

                    <div className="mp-form-group">
                        <label className="mp-form-label">Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            className="mp-form-textarea"
                            placeholder="Describe your product..."
                            required
                            style={{ resize: 'none' }}
                        ></textarea>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ flex: 1, padding: '12px', background: '#F3F4F6', color: '#374151', borderRadius: '12px', border: 'none', fontWeight: '600', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="mp-btn-primary"
                            style={{ flex: 1, justifyContent: 'center' }}
                        >
                            {isSubmitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Listing')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateListingModal;
